import { Button, Group, Loader, Stack, Textarea, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconDownload, IconTrash, IconX } from "@tabler/icons-react";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { TopicParentSelect } from "shared/components/organization/topics/TopicParentSelect";
import { useArchiveTopic } from "shared/components/organization/topics/useArchiveTopic";
import { features } from "shared/graphql/features";
import { topicsQueryOptions, useCreateTopic } from "shared/graphql/topics";
import { usePermissionCheck } from "shared/stores/oidc";
import { maybe } from "shared/utils/maybe";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { Permission, TopicLikeFragment } from "src/gql/graphql";

export type TopicFormType = {
    parent: string;
    name: string;
    description: string;
    required: boolean;
};

const UserCounts = ({ topic }: { topic: TopicLikeFragment }) => {
    const hasOptIn = features.enabled("HAS_WEB_OPT_IN");
    const canViewTopicMembers = usePermissionCheck(Permission.TopicUsersView, topic.id);
    const canViewOptIn = hasOptIn && canViewTopicMembers && topic.isRequired;

    const memberCount = useQuery({ ...topicsQueryOptions.topicMembersCount(topic.id), enabled: canViewTopicMembers });
    const optInCount = useQuery({ ...queries.optInUserMetrics, enabled: canViewOptIn });

    if (!canViewTopicMembers) return null;
    if (memberCount.data?.members == undefined || memberCount.isLoading) return <Loader />;

    return (
        <Group>
            <Title order={3}>{memberCount.data.members} Total Subscribers</Title>
            {canViewOptIn &&
                maybe(optInCount.data?.optInUserMetrics.activeOptInUsersCount)?.take((it) => (
                    <Title order={3}>{it} Public Subscribers</Title>
                ))}
        </Group>
    );
};

export const TopicEditForm = ({
    topic,
    parentId,
    onSubmit,
}: {
    topic: TopicLikeFragment;
    parentId: string;
    onSubmit: (data: TopicFormType) => void;
}) => {
    const nav = useNavigate();

    const { required, category } = useSearch({ from: "/_auth/organization/topics/$topicId" });
    const { archiveTopic } = useArchiveTopic(topic, () => void nav({ to: "/organization/topics" }).catch(notify.catch));

    const form = useForm<TopicFormType>({
        initialValues: {
            parent: category ?? parentId,
            name: topic.name,
            description: topic.description ?? "",
            required: !!required,
        },
    });

    const selectedParentId = form.getValues().parent;

    useEffect(() => {
        form.resetDirty();
    }, [topic]);

    return (
        <form style={{ height: "100%" }} onSubmit={form.onSubmit(onSubmit)}>
            <Stack>
                <TopicParentSelect initialTopic={topic} parentId={selectedParentId} setParentId={(id) => form.setValues({ parent: id })} />
                <TextInput label="Title" required placeholder="Enter a title..." {...form.getInputProps("name")} />
                {!topic.isCategory && (
                    <>
                        <Textarea label="Description" placeholder="Enter a description..." {...form.getInputProps("description")} />
                        <UserCounts topic={topic} />
                    </>
                )}
                <Group justify="space-between">
                    <Button leftSection={<IconTrash size={16} />} onClick={() => void archiveTopic()}>
                        Archive
                    </Button>
                    <Group justify="flex-end">
                        <Button leftSection={<IconDownload size={16} />} type="submit" disabled={!form.isDirty()}>
                            Save
                        </Button>
                        <Button
                            variant="default"
                            leftSection={<IconX size={16} />}
                            onClick={(e) => {
                                e.preventDefault();
                                void nav({ to: "/organization/topics" });
                            }}
                        >
                            Cancel
                        </Button>
                    </Group>
                </Group>
            </Stack>
        </form>
    );
};

export const TopicCreateForm = ({ parentId }: { parentId: string }) => {
    const nav = useNavigate();
    const { required, category } = useSearch({ from: "/_auth/organization/topics/$topicId" });
    const { createTopic } = useCreateTopic(() => void nav({ to: "/organization/topics" }));

    function onSubmit(data: TopicFormType) {
        if (!data.parent) {
            notify.show.error({ message: "Must specify category" });
            return;
        }
        createTopic({
            params: { parentId: data.parent, isCategory: false, isRequired: !!required, name: data.name, description: data.description },
        });
    }

    const form = useForm<TopicFormType>({
        initialValues: {
            parent: category ?? parentId,
            name: "",
            description: "",
            required: !!required,
        },
    });

    const selectedParentId = form.getValues().parent;

    return (
        <form style={{ height: "100%" }} onSubmit={form.onSubmit(onSubmit)}>
            <Stack>
                <TopicParentSelect parentId={selectedParentId} setParentId={(id) => form.setValues({ parent: id })} />
                <TextInput label="Title" required placeholder="Enter a title..." {...form.getInputProps("name")} />
                <Textarea label="Description" placeholder="Enter a description..." {...form.getInputProps("description")} />
                <Group justify="space-between">
                    <Group justify="flex-end">
                        <Button leftSection={<IconDownload size={16} />} type="submit" disabled={!form.isDirty()}>
                            Save
                        </Button>
                        <Button
                            variant="default"
                            leftSection={<IconX size={16} />}
                            onClick={(e) => {
                                e.preventDefault();
                                void nav({ to: "/organization/topics" });
                            }}
                        >
                            Cancel
                        </Button>
                    </Group>
                </Group>
            </Stack>
        </form>
    );
};

const queries = {
    optInUserMetrics: queryOptions({
        queryKey: ["topics", "optInMetrics"],
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query TopicOptInMetrics {
                        optInUserMetrics {
                            activeOptInUsersCount
                        }
                    }
                `),
            ),
    }),
};
